import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authApi } from '../services/api'
import { extractErrorMessage } from '../utils/errorUtils'
import { queryClient } from '../services/queryClient'

const clearAuthSessionAndCache = () => {
  localStorage.removeItem('access_token')
  localStorage.removeItem('user')
  localStorage.removeItem('shopify_shop_domain')
  sessionStorage.removeItem('selected_smb_id')
  queryClient.clear()
}

interface User {
  id: string
  name: string
  email: string
  role: 'agency' | 'smb'
  created_at: string
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  signup: (email: string, password: string, name: string) => Promise<void>
  updateUserFromStorage: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check if user is already authenticated
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const userStr = localStorage.getItem('user')
      
      if (token && userStr) {
        try {
          // First try to get user from stored data
          const storedUser = JSON.parse(userStr)
          setUser(storedUser)
          console.log('AuthContext: User loaded from storage:', storedUser)
          
          // Then verify with API
          const userData = await authApi.getCurrentUser()
          // API returns { user: {...} }, so extract the user object
          const actualUser = userData.user || userData
          setUser(actualUser)
          console.log('AuthContext: User verified with API:', actualUser)
        } catch (apiError) {
          console.error('AuthContext: API verification failed:', apiError)
          // If API fails, still use stored user data
          const storedUser = JSON.parse(userStr)
          setUser(storedUser)
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      // Clear invalid token
      localStorage.removeItem('access_token')
      localStorage.removeItem('user')
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    try {
      console.log('AuthContext: Attempting login for', email)
      const response = await authApi.login(email, password)
      console.log('AuthContext: Login response received', response)
      
      // Purge any stale session data or query cache from previous user
      clearAuthSessionAndCache()

      // Store token and user data
      localStorage.setItem('access_token', response.access_token)
      localStorage.setItem('user', JSON.stringify(response.user))
      
      setUser(response.user)
      console.log('AuthContext: User set in state', response.user)
    } catch (error: any) {
      console.error('AuthContext: Login failed:', error)
      throw new Error(extractErrorMessage(error, 'Login failed'))
    }
  }

  const signup = async (email: string, password: string, name: string) => {
    try {
      const response = await authApi.signup(name, email, password)
      
      // Purge any stale session data or query cache from previous user
      clearAuthSessionAndCache()

      // Store token and user data
      localStorage.setItem('access_token', response.access_token)
      localStorage.setItem('user', JSON.stringify(response.user))
      
      setUser(response.user)
    } catch (error: any) {
      console.error('Signup failed:', error)
      const errObj: any = new Error(extractErrorMessage(error, 'Signup failed'))
      errObj.status = error.response?.status
      throw errObj
    }
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('Logout API call failed:', error)
    } finally {
      // Clear local storage, state, and queryClient cache
      clearAuthSessionAndCache()
      setUser(null)
    }
  }

  const updateUserFromStorage = () => {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        const storedUser = JSON.parse(userStr)
        setUser(storedUser)
        console.log('AuthContext: User updated from storage:', storedUser)
      } catch (error) {
        console.error('AuthContext: Failed to parse stored user:', error)
      }
    }
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    signup,
    updateUserFromStorage,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
